import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { OpenShiftRequest } from '@/api/handlers/resources/shifts/requests';
import type { ShiftResponse } from '@/api/handlers/resources/shifts/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { ShiftQueryKeys } from './shiftQueryKeys';

/**
 * Opens a shift. Once it settles it invalidates the current-shift query so the
 * gate (`useShift().isOpen`) follows the server: on success it flips to open,
 * and a refusal re-reads too, because `SHIFT_ALREADY_OPEN` means the seller's
 * shift was opened on another sale point (SEL-27). The query function never
 * rejects — the caller branches on `result.success` and surfaces the server's
 * 400 `error` (e.g. a rejected co-sign) in a toast.
 */
export const useOpenShiftMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<ShiftResponse>, Error, OpenShiftRequest>({
        mutationFn: (body) => apiClient.shifts.open(body),
        onSettled: () => {
            void queryClient.invalidateQueries({
                queryKey: ShiftQueryKeys.current(),
            });
        },
    });
};
